import type { ComponentType } from 'react';
import * as rawModule from 'echarts-for-react/esm/core';
import type { EChartsReactProps } from 'echarts-for-react/esm/types';

import { cn } from '../../lib/cn';

/* =============================================================================
   ECharts binding — interop shim
   -----------------------------------------------------------------------------
   Why this file exists.

   `echarts-for-react` ships both a CJS build (`lib/`) and an ESM build (`esm/`)
   but declares no `exports` map in its package.json. Importing the documented
   CJS path (`echarts-for-react/lib/core`) therefore resolves to
   `exports.default = EChartsReactCore` with `__esModule: true`, and under a
   bundler's interop that default can arrive either as the component itself or
   wrapped one level deeper as an object.

   The failure mode is severe and non-obvious: rendering the wrapped object
   throws "Element type is invalid: expected a string ... but got: object", and
   because it throws *during render*, React tears down the whole tree — one
   broken chart blanked the entire showcase page rather than showing an empty
   chart box.

   So two things happen here:
     1. The import targets the real ESM build, which has a plain
        `export default`.
     2. The value is unwrapped defensively, so a future change to the package's
        module layout cannot reintroduce the blank page.

   Importing this module means every chart shares one resolution and one
   fallback, instead of six copies of the same hazard.
   ============================================================================= */

/** Unwraps a module default that may be nested under one or more `default` keys. */
function unwrapComponent(value: unknown): unknown {
  let current: unknown = value;
  // Bounded: three levels is already more than any real interop shape produces,
  // and an unbounded walk could spin on a self-referential module object.
  for (let depth = 0; depth < 3; depth += 1) {
    if (typeof current === 'function') return current;
    if (current && typeof current === 'object' && 'default' in current) {
      current = (current as { default: unknown }).default;
      continue;
    }
    break;
  }
  return current;
}

const Resolved = unwrapComponent(rawModule);

/** True when the binding resolved to a real component. */
export const isEChartResolved: boolean = typeof Resolved === 'function';

const ResolvedChart = Resolved as ComponentType<EChartsReactProps>;

/**
 * The ECharts React binding, resolved.
 *
 * When the binding fails to resolve, this renders a readable, announced message
 * instead of throwing — a chart that cannot render must degrade to an error
 * box, never take the page with it.
 */
export function EChart(props: EChartsReactProps) {
  if (!isEChartResolved) {
    return (
      <div
        role="alert"
        className={cn(
          'flex min-h-[240px] w-full items-center justify-center rounded-xs',
          'border border-danger bg-danger-surface p-6 text-center',
        )}
      >
        <p className="max-w-[52ch] text-sm text-danger-on">
          The chart library failed to load, so this chart cannot be drawn. The
          figure is still available in the data table beside it.
        </p>
      </div>
    );
  }

  // No `role="img"` here: this shim never receives the chart's name, and an
  // unnamed image is an axe `role-img-alt` violation. The image semantics —
  // `role="img"`, `aria-label`, `aria-describedby` — belong to the one layer
  // that has them, `ChartFrame` (ChartFrame.tsx), which drops the role entirely
  // for a chart that carries its own semantics.
  // `h-full` is load-bearing, not decoration. `ChartFrame` reserves an explicit
  // height (default 320px) and expects the chart to fill it, but ECharts sizes
  // itself from its container: with no height here, `height: 100%` resolved
  // against an auto-height parent and ECharts fell back to its own 100px
  // default. Measured before the fix: canvases rendered 1028x100 inside a
  // 320px frame, leaving two thirds of every chart panel blank and crushing the
  // axis labels into an unreadable smear. A shim that drops the height silently
  // un-sizes every chart that uses it.
  return (
    <div className="h-full w-full">
      <ResolvedChart {...props} />
    </div>
  );
}
